<template>
  <v-container class="ma-0 px-4 pt-4" fill-height fluid>
    <v-row justify-space-between>
      <v-col xs="12" sm="10" md="8" lg="6" xl="5">
        <v-row>
          <v-col>
            <v-card class="card" :height="contractInfoCard">
              <v-card-title :class="title">
                Contract Info
                <v-spacer />
                <v-menu left offset-y open-on-hover>
                  <template #activator="{ on }">
                    <v-btn
                      :class="body"
                      text
                      rounded
                      color="accent"
                      dark
                      slot="activator"
                      v-on="on">
                      <v-icon left>mdi-information</v-icon>Vendor Info
                    </v-btn>
                  </template>
                  <v-card>
                    <v-container class="py-4">
                      <v-row
                        v-for="(key, index) in vendorInfoDisplay"
                        :key="index"
                        align="center"
                        dense>
                        <v-list-item dense>
                          <v-list-item-content>
                            <v-list-item-subtitle
                              :class="label"
                              class="black--text"
                              >{{ index }}</v-list-item-subtitle
                            >
                            <v-list-item-title :class="body">
                              {{ key && key.length > 1 ? key : 'N/A' }}
                            </v-list-item-title>
                          </v-list-item-content>
                        </v-list-item>
                      </v-row>
                    </v-container>
                  </v-card>
                </v-menu>
              </v-card-title>
              <v-divider />
              <v-container class="pa-2">
                <v-form ref="form">
                  <v-row dense class="inputRow">
                    <v-col>
                      <v-text-field
                        :readonly="readonly"
                        outlined
                        dense
                        background-color="input"
                        :class="body"
                        prepend-inner-icon="mdi-note-text-outline"
                        label="Description"
                        placeholder="Description to Identify Contract"
                        :rules="[v => !!v || 'Description Required']"
                        v-model="contract.description" />
                    </v-col>
                  </v-row>
                  <v-row dense class="inputRow">
                    <v-col md="7" lg="7" xl="7">
                      <v-autocomplete
                        :readonly="readonly"
                        auto-select-first
                        :class="body"
                        outlined
                        dense
                        background-color="input"
                        :items="storeTypes"
                        item-value="storeType"
                        item-text="storeTypeDescription"
                        v-model="contract.store_type"
                        prepend-inner-icon="mdi-store"
                        label="Store Type"
                        placeholder="Select Store Type"
                        @change="checkForExistingItems"
                        :menu-props="{ rounded: '' }"
                        :rules="[v => !!v || 'Store Type Required']" />
                    </v-col>
                    <v-col md="5" lg="5" xl="5">
                      <v-autocomplete
                        :readonly="readonly"
                        :disabled="!contract.status"
                        :class="body"
                        outlined
                        dense
                        :prepend-inner-icon="getStatusIcons()"
                        background-color="input"
                        :items="statuses"
                        item-text="name"
                        item-value="value"
                        label="Status"
                        v-model="contract.status">
                      </v-autocomplete>
                    </v-col>
                  </v-row>
                  <v-row dense class="inputRow" align="center">
                    <v-col cols="2">
                      <v-checkbox
                        class="ml-2 pt-2"
                        v-model="contract.couponflag"
                        label="Coupon" />
                    </v-col>
                    <v-col>
                      <v-textarea
                        :readonly="readonly"
                        background-color="input"
                        dense
                        outlined
                        hide-details
                        label="Coupon Comment"
                        prepend-inner-icon="mdi-note-text-outline"
                        rows="2"
                        no-resize
                        v-model="contract.couponcomment">
                      </v-textarea>
                    </v-col>
                  </v-row>
                  <v-row>
                    <v-col align="end">
                      <span
                        v-if="contract.created_on"
                        class="subtitle-2 font-weight-light">
                        Created On: {{ formatDateDisplay(contract.created_on) }}
                      </span>
                    </v-col>
                  </v-row>
                </v-form>
              </v-container>
            </v-card>
          </v-col>
        </v-row>
        <v-row justify-space-between>
          <v-col>
            <v-card class="card">
              <v-card-title :class="title">
                Contract Types
                <v-spacer />
              </v-card-title>
              <v-divider />
              <v-container class="ma-0 pa-0" fluid>
                <v-tabs
                  vertical
                  color="primary"
                  active-class="active"
                  height="239"
                  background-color="fill"
                  hide-slider
                  v-model="tab">
                  <v-tab
                    :disabled="
                      (newItemContract && type.contract_type !== 'New Item') ||
                      (costChangeContract &&
                        type.contract_type !== 'Cost Change')
                    "
                    :class="body"
                    v-for="(type, index) in contractTypes"
                    :key="index"
                    style="
                      height: 60px;
                      width: 230px;
                      border-bottom: 1px solid #d1d1d1;
                    "
                    :style="tab === 3 ? 'border-bottom-left-radius:3px;' : ''">
                    <v-checkbox
                      :disabled="readonly"
                      :class="tab === index ? 'white--text mt-5' : 'mt-5'"
                      :color="tab === index ? 'white' : 'primary'"
                      @click.stop="tab = index"
                      v-model="type.active" />
                    <span class="ml-4 mr-6">{{ type.contract_type }}</span>
                    <v-spacer />
                    <v-tooltip v-if="type.active && index !== tab" top>
                      <template #activator="{ on }">
                        <v-icon
                          v-on="on"
                          :color="
                            type.active && !hasRequiredParams(type)
                              ? 'red'
                              : 'green'
                          "
                          class="pr-2"
                          dense>
                          {{
                            type.active && !hasRequiredParams(type)
                              ? 'mdi-progress-alert'
                              : 'mdi-checkbox-marked-circle-outline'
                          }}
                        </v-icon>
                      </template>
                      <span>{{
                        type.active && !hasRequiredParams(type)
                          ? 'Missing Required Fields'
                          : 'Complete'
                      }}</span>
                    </v-tooltip>
                    <v-icon v-if="tab === index">mdi-chevron-right</v-icon>
                  </v-tab>
                  <v-tabs-items
                    v-model="tab"
                    class="card"
                    style="border-left: 1px solid #d1d1d1 !important">
                    <v-tab-item
                      v-for="(type, index) in contractTypes"
                      :key="index"
                      :transition="false"
                      class="card">
                      <v-container class="card ma-0 px-4 pt-4" fluid>
                        <v-row
                          v-for="(row, index) in attributeRows(type)"
                          :key="index"
                          align="center"
                          dense
                          style="height: 63px">
                          <v-col
                            v-for="(input, index) in row"
                            :key="index"
                            cols="6">
                            <Input
                              v-if="input.data_type === 'string'"
                              :input="input"
                              :readonly="readonly"
                              :tab="tab"
                              :type="type"
                              @update="input.value = $event" />
                            <Date
                              v-if="input.data_type === 'date'"
                              :dates="getDates(row)"
                              :input="input"
                              :readonly="readonly"
                              :tab="tab"
                              :type="type" />
                          </v-col>
                        </v-row>
                      </v-container>
                    </v-tab-item>
                  </v-tabs-items>
                </v-tabs>
              </v-container>
            </v-card>
          </v-col>
        </v-row>
      </v-col>
      <v-col xs="12" sm="10" md="8" lg="6" xl="7">
        <v-card class="card" :height="activityCard">
          <v-card-title :class="title">
            Contract Activity / Comments
            <v-spacer />
            <v-menu
              v-model="menu"
              :close-on-content-click="false"
              :nudge-width="500"
              offset-x
              offset-y
              left>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  :disabled="readonly || !savedContract"
                  rounded
                  :class="body"
                  class="body"
                  color="primary"
                  v-bind="attrs"
                  v-on="on">
                  <v-icon left>mdi-comment-plus-outline</v-icon> Add Comment
                </v-btn>
              </template>
              <v-card class="card">
                <v-container class="pa-6">
                  <v-row>
                    <v-col>
                      <label :class="label">Comment</label>
                      <v-textarea
                        :class="body"
                        hide-details
                        outlined
                        solo
                        no-resize
                        rows="3"
                        label="Leave a comment..."
                        background-color="input"
                        @keydown.enter="addComment"
                        v-model="message">
                      </v-textarea>
                    </v-col>
                  </v-row>
                </v-container>
                <v-card-actions>
                  <v-spacer />
                  <v-btn :class="body" rounded text @click="menu = false">
                    Cancel
                  </v-btn>
                  <v-btn
                    :class="body"
                    class="primary white--text"
                    rounded
                    @click="addComment">
                    Save
                  </v-btn>
                </v-card-actions>
              </v-card>
            </v-menu>
          </v-card-title>
          <v-divider />
          <v-container class="ma-0 py-0 card" :style="timelineHeight" fluid>
            <v-row class="ma-0 pa-0">
              <v-col class="ma-0 pa-0">
                <span
                  class="black--text text-body-1 font-weight-light ml-1"
                  v-for="(day, index) in groupedDays"
                  :key="index">
                  {{ moment(index).format('MMMM Do') }}
                  <v-timeline v-if="isExistingContract" dense>
                    <v-spacer />
                    <v-timeline-item
                      fill-dot
                      v-for="(i, index) in day"
                      :key="index"
                      :color="getIconColor(i.message_type)">
                      <template v-slot:icon>
                        <v-icon small color="white">{{
                          getIcon(i.message_type)
                        }}</v-icon>
                      </template>
                      <v-row
                        v-if="comments.length && i.message_type === 'status'"
                        dense>
                        <v-col class="pt-2">
                          <span
                            class="black--text text-body-2 font-weight-medium"
                            >{{ i.name }}</span
                          >
                          <span
                            class="black--text text-body-2 font-weight-light">
                            {{
                              i.timestamp == contractCreation.timestamp
                                ? `created contract`
                                : `updated status to ${getStatusName(
                                    i.message
                                  )}.`
                            }}
                          </span>
                        </v-col>
                        <v-col align="right">
                          <span
                            class="
                              black--text
                              text-body-2
                              font-weight-light
                              mr-4
                            "
                            >{{ formatTimestamp(i.timestamp) }}</span
                          >
                        </v-col>
                      </v-row>
                      <v-card
                        v-if="comments.length && i.message_type === 'comment'"
                        class="elevation-2 rounded-lg my-1 mr-2">
                        <v-card-title
                          class="pb-0 text-body-2 font-weight-medium">
                          {{ i.name }}
                        </v-card-title>
                        <v-card-text
                          class="
                            black--text
                            text-body-2
                            font-weight-light
                            mt-2
                          ">
                          {{ i.message }}
                        </v-card-text>
                        <v-card-actions>
                          <v-spacer />
                          <span class="text-body-2 font-weight-light">{{
                            formatTimestamp(i.timestamp)
                          }}</span>
                        </v-card-actions>
                      </v-card>
                    </v-timeline-item>
                  </v-timeline>
                </span>
              </v-col>
            </v-row>
            <v-container v-if="!isExistingContract" class="pl-5">
              <v-row justify="center" align="center" dense>
                <v-col>
                  <span :class="label" class="mt-5 text-capitalize">
                    No Activity
                  </span>
                </v-col>
              </v-row>
            </v-container>
          </v-container>
        </v-card>
      </v-col>
    </v-row>
    <StoreTypeAlert
      :value.sync="storeTypeAlert"
      @cancel="getContract"
      @confirm="changeStoreType"
      :contract="contract" />
  </v-container>
</template>

<script>
import { format } from '@/mixins/format'
import { displayHelpers } from '@/mixins/display-helpers'
import { mapState } from 'vuex'

export default {
  name: 'ContractDetails',

  components: {
    Input: () => import('@/components/inputs/Input'),
    Date: () => import('@/components/inputs/Date'),
    StoreTypeAlert: () => import('@/components/contract/alerts/StoreTypeAlert')
  },

  props: {
    vendor: Object,
    vendorInfoDisplay: Object,
    contract: Object,
    isExistingContract: Boolean,
    getContract: Function,
    isBuyer: Boolean,
    readonly: Boolean
  },

  mixins: [format, displayHelpers],

  data: () => ({
    activeTypes: [],
    validContractTypes: false,
    storeTypeAlert: false,
    menu: false,
    message: '',
    tab: null,
    invalid: null
  }),

  computed: {
    ...mapState([
      'allStores',
      'addedItems',
      'contractTypes',
      'contractStatus',
      'comments',
      'storeTypes',
      'currentVendor'
    ]),

    contractCreation() {
      return this._.minBy(this.comments, 'timestamp')
    },

    groupedDays() {
      return this._.groupBy(
        this.comments.map(comment => {
          comment.day = this.moment(comment.timestamp).format('YYYY-MM-DD')
          return comment
        }),
        'day'
      )
    },

    newItemContract() {
      return this.activeTypes.some(type => type.contract_type === 'New Item')
    },

    costChangeContract() {
      return this.activeTypes.some(type => type.contract_type === 'Cost Change')
    },

    savedContract() {
      return !this._.isEmpty(this.contract.contract_uuid)
    },

    statuses() {
      const vendorOptions = [
        { name: 'Declined', value: 'declined' },
        { name: 'Draft', value: 'draft' },
        { name: 'Submitted', value: 'submitted' }
      ]
      const buyerOptions = [
        { name: 'Draft', value: 'draft' },
        { name: 'Declined', value: 'declined' },
        { name: 'Submitted', value: 'submitted' },
        { name: 'Approved', value: 'approved' },
        { name: 'POS Pending', value: 'pos_pending' },
        { name: 'POS Generated', value: 'pos_generated' }
      ]
      if (this.$auth.isBuyer) {
        return buyerOptions
      } else {
        return vendorOptions
      }
    }
  },

  watch: {
    contractTypes: {
      deep: true,
      handler() {
        this.activeTypes = this.contractTypes.filter(type => type.active)
        this.$store.commit('SET_CONTRACT_TYPES', this.contractTypes)
        const allFields = this._.flatten(
          this.activeTypes.map(activeType => activeType.parameters)
        )
        const allRequiredFields = allFields.filter(field => field.required)
        const allRequiredWithValue = allRequiredFields.filter(
          requiredField => !this._.isEmpty(requiredField.value)
        )
        if (allRequiredFields.length == allRequiredWithValue.length) {
          this.validContractTypes = true
        } else {
          this.validContractTypes = false
        }
      }
    },

    activeTypes: {
      handler() {
        if (!this.activeTypes.length) {
          this.validContractTypes = false
        }
        const activeTypeNames = this.activeTypes.map(type => type.contract_type)
        this.contractTypes.map(type => {
          if (
            (activeTypeNames.includes('New Item') &&
              type.contract_type !== 'New Item') ||
            (activeTypeNames.includes('Cost Change') &&
              type.contract_type !== 'Cost Change')
          ) {
            type.active = false
            type.parameters.map(param => {
              param.value = ''
            })
          }
        })
        if (!this.activeTypes.length) {
          this.$refs.form.resetValidation()
        }
      }
    },

    modal: {
      immediate: true,
      handler() {
        this.componentKey += 1
      }
    }
  },

  created() {
    if (this.isExistingContract) {
      this.hasRequiredValues = true
    } else {
      this.hasRequiredValues = false
    }
    this.eventbus.$on('create', this.createContract)
    this.eventbus.$on('update', this.updateContract)
  },

  methods: {
    getStatusName(status) {
      switch (status) {
        case 'draft':
          return 'draft'
        case 'submitted':
          return 'submitted'
        case 'approved':
          return 'approved'
        case 'declined':
          return 'declined'
        case 'pos_pending':
          return 'POS pending'
        case 'pos_generated':
          return 'POS generated'
      }
    },

    hasRequiredParams(type) {
      const requiredParams = type.parameters.reduce((acc, result) => {
        if (result.required) {
          acc.push(result)
        }
        return acc
      }, [])

      const requiredParamValues = type.parameters.reduce((acc, result) => {
        if (result.required && result.value) {
          acc.push(result)
        }
        return acc
      }, [])

      return requiredParamValues.length >= requiredParams.length
    },

    getRows() {
      const numCols = 8
      const rows = this.itemKeys.reduce((acc, curr, index) => {
        const i = index
        const row = Math.floor(i / numCols)
        const mod = i % numCols
        if (mod === 0) {
          acc.push([])
        }
        acc[row].push(curr)
        return acc
      }, [])
      return rows
    },

    getTypes() {
      const group = this.contractTypes.reduce(function (
        result,
        value,
        index,
        array
      ) {
        if (index % 2 === 0) result.push(array.slice(index, index + 2))
        return result
      },
      [])
      return group
    },

    attributeRows(type) {
      return this._.groupBy(type.parameters, 'group')
    },

    getDates(row) {
      const dates = row.filter(r => r.data_type === 'date')
      return dates
    },

    getStatusIcons() {
      switch (this.contract.status) {
        case 'draft':
          return 'mdi-progress-pencil'
        case 'submitted':
          return 'mdi-progress-check'
        case 'approved':
          return 'mdi-checkbox-marked-circle-outline'
        case 'declined':
          return 'mdi-progress-alert'
        case 'pos_pending':
          return 'mdi-progress-clock'
        case 'pos_generated':
          return 'mdi-cash-register'
      }
    },

    getIcon(key) {
      switch (key) {
        case 'status':
          return 'mdi-cog'
        case 'comment':
          return 'mdi-message-text'
        default:
          break
      }
    },

    getIconColor(key) {
      switch (key) {
        case 'status':
          return 'grey'
        case 'comment':
          return 'green'
        default:
          break
      }
    },

    checkForExistingItems() {
      if (this.addedItems.length > 0) {
        this.storeTypeAlert = true
      } else {
        this.changeStoreType()
      }
    },

    async changeStoreType() {
      await this.$store.dispatch('getStoresByType', this.contract)
      if (this.addedItems.length > 0) {
        await this.$store.dispatch('removeStoreTypeItems', this.contract)
      }
      await this.updateContract()
    },

    async getContractTypes() {
      try {
        const { data } = await this.$contracts.getTypes(
          this.contract.contract_uuid
        )
        this.contractTypes = this._.orderBy(data, ['type_index'], ['asc'])
        this.$store.commit('SET_CONTRACT_TYPES', this.contractTypes)
      } catch {
        this.$store.dispatch('setSnackbar', {
          status: 'error',
          text: 'Error Retrieving Contract Types'
        })
      }
    },

    async addComment() {
      this.menu = false
      const comment = {
        contract_uuid: this.contract.contract_uuid,
        message: this.message,
        message_type: 'comment'
      }
      try {
        await this.$contracts.saveContractComment(comment)
        this.$store.dispatch('setSnackbar', {
          status: 'success',
          text: 'Added Comment'
        })
        this.message = ''
      } catch {
        this.$store.dispatch('setSnackbar', {
          status: 'error',
          text: 'Failed to Add Comment'
        })
      } finally {
        this.$store.dispatch('getComments', this.contract)
      }
    },

    async saveContractTypes(contractUUID) {
      this.contractTypes.forEach(type => (type.contract_uuid = contractUUID))
      try {
        await this.$contracts.saveContractTypes(this.contractTypes)
      } catch {
        this.$store.dispatch('setSnackbar', {
          status: 'error',
          text: 'Error Saving Contract Types'
        })
      }
    },

    async createContract() {
      const valid = this.$refs.form.validate()
      this.contract.vendor_number = this.currentVendor.vendor_number

      if (valid && this.validContractTypes) {
        try {
          this.$store.dispatch('setOverlay', {
            status: 'show',
            text: 'Creating...'
          })
          const response = await this.$contracts.createContract(this.contract)
          this.contract.contract_uuid = response.data.contract_uuid
          localStorage.setItem(
            'contract_uuid',
            JSON.stringify(this.contract.contract_uuid)
          )
          this.saveContractTypes(this.contract.contract_uuid)
          this.$router.replace({ query: { id: this.contract.contract_uuid } })
          this.$store.dispatch('setSnackbar', {
            status: 'success',
            text: `${this.contract.description} Created`
          })
        } catch {
          this.$store.dispatch('setSnackbar', {
            status: 'error',
            text: 'Error Creating Contract'
          })
        } finally {
          await this.getContract()
        }
      } else {
        this.$store.dispatch('setSnackbar', {
          status: 'error',
          text: 'Missing Required Fields'
        })
      }
    },

    async updateContract() {
      const valid = this.$refs.form.validate()
      const status = {
        contract_uuid: this.contract.contract_uuid,
        message: this.contract.status,
        message_type: 'status'
      }
      if (valid && this.validContractTypes) {
        try {
          this.$store.dispatch('setOverlay', {
            status: 'show',
            text: 'Updating...'
          })
          const contract = this.$contracts.updateContract(
            this.contract.contract_uuid,
            this.contract
          )
          const contractTypes = this.$contracts.saveContractTypes(
            this.contractTypes
          )
          const requests = [contract, contractTypes]
          if (this.contract.status !== this.contractStatus) {
            const contractStatus = this.$contracts.saveContractComment(status)
            requests.push(contractStatus)
          }
          await Promise.allSettled(requests)
          if (!this.$auth.isBuyer && this.contract.status === 'submitted')
            this.$store.commit('SET_READONLY', true)
          this.$store.dispatch('setSnackbar', {
            status: 'success',
            text: `${this.contract.description} Updated`
          })
        } catch {
          this.$store.dispatch('setSnackbar', {
            status: 'error',
            text: 'Error Updating Contract'
          })
        } finally {
          await this.$store.dispatch('getComments', this.contract)
          this.$store.commit('SET_CONTRACT_STATUS', this.contract.status)
          this.$store.dispatch('setOverlay', { status: 'hide' })
        }
      } else {
        this.$store.dispatch('setSnackbar', {
          status: 'error',
          text: 'Missing Required Fields'
        })
      }
    }
  },

  beforeDestroy() {
    this.eventbus.$off('create')
    this.eventbus.$off('update')
  }
}
</script>

<style scoped>
.inputRow {
  height: 60px;
}
.active {
  /* box-shadow: 0px 4px 4px 0px rgba(120,120,120,0.75); */
  background: #385f73;
  color: #fff !important;
}
.dialog {
  position: absolute;
  bottom: 0;
  right: 0;
}
</style>
